import { useMemo, type ReactNode } from "react";
import { ChevronDown, RotateCcw, Trash2 } from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { VARIABLE_TYPES } from "@/domains/tenant/variable-types";
import {
  variableToInputFieldDefinition,
  type InputFieldEditorRow,
} from "@/domains/templates/input-fields";
import type {
  LinkedVariableOption,
  UiVariableGroup,
  Variable,
} from "@/domains/templates/types";
import { cn } from "@/shared/lib/utils";
import {
  areTemplateDefaultsEqual,
  withDefinitionDefault,
} from "@/domains/templates/default-values";

type TemplateInputFieldEditorRowProps = {
  field: InputFieldEditorRow;
  index: number;
  variableScope?: "catalog" | "analysis";
  analysisId?: string;
  isReadOnly?: boolean;
  isDraft?: boolean;
  layout?: "row" | "form";
  onChange: (index: number, field: InputFieldEditorRow) => void;
  onRemove?: (index: number) => void;
  data?: TemplateInputFieldEditorData;
};

export type TemplateInputFieldEditorData = {
  currentTenantId?: string;
  catalogVariableGroups: UiVariableGroup[];
  analysisVariables: Variable[];
  linkedAnalyses: LinkedVariableOption[];
  linkedAnalysisSteps: LinkedVariableOption[];
  isLoading?: boolean;
};

type VariableSourceLevel = "Global" | "Tenant" | "Analysis" | "Custom";

type VariableSelectOption = {
  variable: Variable;
  sourceLevel: VariableSourceLevel;
};

const VARIABLE_SOURCE_ORDER: Record<VariableSourceLevel, number> = {
  Global: 0,
  Tenant: 1,
  Analysis: 2,
  Custom: 3,
};

const EMPTY_VARIABLES: Variable[] = [];
const EMPTY_VARIABLE_GROUPS: UiVariableGroup[] = [];

function variableLabel(variable: Variable) {
  return variable.variable_name_frontend ?? variable.variable_name;
}

function defaultValueToText(value: InputFieldEditorRow["defaultValue"]) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return JSON.stringify(value);
}

function defaultPlaceholder(variable: Variable | undefined) {
  if (!variable || variable.variable_default == null) return "No default";
  if (typeof variable.variable_default === "string") {
    return variable.variable_default;
  }
  return JSON.stringify(variable.variable_default);
}

function jsonArrayToStringArray(value: InputFieldEditorRow["defaultValue"]) {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function modalDefaultInputType(type: InputFieldEditorRow["type"]) {
  if (type === "number" || type === "integer") return "number";
  if (type === "date") return "date";
  if (type === "datetime") return "datetime-local";
  return "text";
}

function modalDefaultFromText(
  type: InputFieldEditorRow["type"],
  value: string,
): InputFieldEditorRow["defaultValue"] {
  if (!value.trim()) return undefined;
  if (type === "number" || type === "integer") return Number(value);
  if (type === "array") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return value;
}

function hasLinkedTarget(variable: Variable) {
  return Boolean(
    variable.variable_linked_schema &&
    variable.variable_linked_table &&
    variable.variable_linked_column_id,
  );
}

function isAnalysisScopedGlobalVariable(variable: Variable) {
  return hasLinkedTarget(variable) || variable.variable_kind === "tool";
}

function linkedTableForField(
  field: InputFieldEditorRow,
  variable: Variable | undefined,
) {
  return field.variableLinkedTable ?? variable?.variable_linked_table ?? null;
}

function applyVariable(
  field: InputFieldEditorRow,
  variable: Variable,
): InputFieldEditorRow {
  return {
    ...field,
    ...variableToInputFieldDefinition(variable),
    id: field.id,
  };
}

export function TemplateInputFieldEditorRow({
  field,
  index,
  variableScope = "catalog",
  isReadOnly = false,
  isDraft = false,
  layout = "row",
  onChange,
  onRemove,
  data,
}: TemplateInputFieldEditorRowProps) {
  const currentTenantId = data?.currentTenantId;
  const catalogVariableGroups =
    data?.catalogVariableGroups ?? EMPTY_VARIABLE_GROUPS;
  const analysisVariables = data?.analysisVariables ?? EMPTY_VARIABLES;
  const variablesLoading = data?.isLoading ?? false;
  const rootVariables = useMemo(
    () =>
      variableScope === "analysis"
        ? analysisVariables.filter(
            (variable) => variable.parent_variable_id === null,
          )
        : catalogVariableGroups
            .flatMap((group) => group.variables)
            .filter(
              (variable) => variable.variable_linked_table !== "analysisstep",
            ),
    [analysisVariables, catalogVariableGroups, variableScope],
  );
  const globalAnalysisScopeVariables = useMemo(
    () =>
      catalogVariableGroups
        .flatMap((group) => group.variables)
        .filter(
          (variable) =>
            Boolean(currentTenantId) &&
            variable.record_tenant_id !== currentTenantId &&
            isAnalysisScopedGlobalVariable(variable),
        ),
    [catalogVariableGroups, currentTenantId],
  );
  const tenantAnalysisScopeVariables = useMemo(
    () =>
      catalogVariableGroups
        .flatMap((group) => group.variables)
        .filter(
          (variable) =>
            Boolean(currentTenantId) &&
            variable.record_tenant_id === currentTenantId,
        ),
    [catalogVariableGroups, currentTenantId],
  );
  const variableSelectOptions = useMemo<VariableSelectOption[]>(() => {
    const options =
      variableScope === "analysis"
        ? [
            ...rootVariables.map((variable) => ({
              variable,
              sourceLevel: "Analysis" as const,
            })),
            ...globalAnalysisScopeVariables.map((variable) => ({
              variable,
              sourceLevel: "Global" as const,
            })),
            ...tenantAnalysisScopeVariables.map((variable) => ({
              variable,
              sourceLevel: "Tenant" as const,
            })),
          ]
        : rootVariables.map((variable) => ({
            variable,
            sourceLevel:
              currentTenantId && variable.record_tenant_id === currentTenantId
                ? ("Tenant" as const)
                : ("Global" as const),
          }));

    return options
      .map((option, originalIndex) => ({ ...option, originalIndex }))
      .sort(
        (left, right) =>
          VARIABLE_SOURCE_ORDER[left.sourceLevel] -
            VARIABLE_SOURCE_ORDER[right.sourceLevel] ||
          left.originalIndex - right.originalIndex,
      );
  }, [
    currentTenantId,
    rootVariables,
    globalAnalysisScopeVariables,
    tenantAnalysisScopeVariables,
    variableScope,
  ]);
  const selectableVariables = useMemo(
    () => variableSelectOptions.map((option) => option.variable),
    [variableSelectOptions],
  );
  const selectedVariable = selectableVariables.find(
    (variable) => variable.variable_id === field.sourceVariableId,
  );
  const linkedTable = linkedTableForField(field, selectedVariable);
  const linkedOptions =
    linkedTable === "analysis"
      ? (data?.linkedAnalyses ?? [])
      : linkedTable === "analysisstep"
        ? (data?.linkedAnalysisSteps ?? [])
        : [];
  const linkedOptionsLoading = data?.isLoading ?? false;
  const rowDisabled = isReadOnly;
  const displayName = field.variableNameFrontend ?? field.key ?? "";
  const isLinkedVariable =
    field.source === "variable" && Boolean(field.sourceVariableId);
  const metadataDisabled = rowDisabled || isLinkedVariable;
  const canResetDefault =
    !rowDisabled &&
    isLinkedVariable &&
    selectedVariable !== undefined &&
    !areTemplateDefaultsEqual(
      field.defaultValue,
      selectedVariable.variable_default,
    );

  const updateField = (nextField: InputFieldEditorRow) => {
    onChange(index, nextField);
  };

  const presentField = (label: string, control: ReactNode) =>
    layout === "form" ? (
      <div className="grid gap-2 sm:grid-cols-[160px_1fr] sm:items-center">
        <Label>{label}</Label>
        <div className="min-w-0">{control}</div>
      </div>
    ) : (
      control
    );

  return (
    <div
      className={cn(
        layout === "form"
          ? "grid gap-3 border-0 bg-transparent p-0"
          : "grid gap-2 rounded-md border bg-background p-2 lg:grid-cols-[112px_minmax(160px,1.15fr)_112px_minmax(180px,1fr)_minmax(150px,0.85fr)_88px] lg:items-center",
        isDraft && layout === "row" && "border-dashed",
      )}
    >
      {presentField(
        "Source",
        <Select
          value={field.source ?? "custom"}
          onValueChange={(value) =>
            updateField({
              ...field,
              source: value === "variable" ? "variable" : "custom",
              ...(value === "custom"
                ? {
                    variableId: undefined,
                    sourceVariableId: undefined,
                    variableGroup: undefined,
                    variableLinkedSchema: undefined,
                    variableLinkedTable: undefined,
                    variableLinkedColumnId: undefined,
                    variableLinkedColumnName: undefined,
                    parentVariableId: null,
                  }
                : {}),
            })
          }
          disabled={rowDisabled}
        >
          <SelectTrigger aria-label="Mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="custom">Custom</SelectItem>
            <SelectItem value="variable">Variable</SelectItem>
          </SelectContent>
        </Select>,
      )}

      {presentField(
        field.source === "variable" ? "Variable" : "Label",
        field.source === "variable" ? (
          <Select
            value={field.sourceVariableId ?? ""}
            onValueChange={(variableId) => {
              const variable = selectableVariables.find(
                (candidate) => candidate.variable_id === variableId,
              );
              if (variable) updateField(applyVariable(field, variable));
            }}
            disabled={rowDisabled || variablesLoading}
          >
            <SelectTrigger aria-label="Variable Name">
              <SelectValue
                placeholder={
                  variablesLoading ? "Loading..." : "Select variable"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {variableSelectOptions.map(({ variable, sourceLevel }) => (
                <SelectItem
                  key={variable.variable_id}
                  value={variable.variable_id}
                >
                  <span className="flex w-16 shrink-0 items-center">
                    <Badge
                      className={cn(
                        "h-5 px-1.5 text-[10px]",
                        sourceLevel === "Tenant" && "bg-primary/70",
                        sourceLevel === "Analysis" &&
                          "bg-primary/20 text-primary",
                        sourceLevel === "Custom" &&
                          "bg-muted text-muted-foreground",
                      )}
                    >
                      {sourceLevel}
                    </Badge>
                  </span>
                  <span className="truncate">{variableLabel(variable)}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={displayName}
            onChange={(event) =>
              updateField({
                ...field,
                key: event.target.value,
                variableNameFrontend: event.target.value,
              })
            }
            readOnly={rowDisabled}
            disabled={rowDisabled}
            placeholder="Display name"
            aria-label="Variable display name"
          />
        ),
      )}

      {presentField(
        "Type",
        <Select
          value={field.type}
          onValueChange={(value) =>
            updateField({
              ...field,
              type: value as InputFieldEditorRow["type"],
              ...(value === "array" ? {} : { variableOptions: [] }),
              ...(layout === "form" && value !== field.type
                ? { defaultValue: undefined }
                : {}),
            })
          }
          disabled={metadataDisabled}
        >
          <SelectTrigger aria-label="Input Field Type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VARIABLE_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>,
      )}

      {presentField(
        "Description",
        <Input
          value={field.description ?? ""}
          onChange={(event) =>
            updateField({ ...field, description: event.target.value })
          }
          readOnly={metadataDisabled}
          disabled={metadataDisabled}
          placeholder="Description"
          aria-label="Description"
        />,
      )}

      {presentField(
        "Default value",
        <div className="flex min-w-0 items-center gap-1">
          <div className="min-w-0 flex-1">
            {linkedTable === "analysis" || linkedTable === "analysisstep" ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-between"
                    disabled={rowDisabled || linkedOptionsLoading}
                    aria-label="Default Value"
                  >
                    <span className="truncate text-left">
                      {jsonArrayToStringArray(field.defaultValue).length > 0
                        ? linkedOptions
                            .filter((option) =>
                              jsonArrayToStringArray(
                                field.defaultValue,
                              ).includes(option.id),
                            )
                            .map((option) => option.name)
                            .join(", ")
                        : linkedOptionsLoading
                          ? "Loading..."
                          : "Select references"}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-60" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-[var(--radix-dropdown-menu-trigger-width)]"
                >
                  {linkedOptions.map((option) => {
                    const selectedValues = jsonArrayToStringArray(
                      field.defaultValue,
                    );
                    const checked = selectedValues.includes(option.id);
                    return (
                      <DropdownMenuCheckboxItem
                        key={option.id}
                        checked={checked}
                        onCheckedChange={(nextChecked) => {
                          const nextValues =
                            nextChecked === true
                              ? [...new Set([...selectedValues, option.id])]
                              : selectedValues.filter(
                                  (value) => value !== option.id,
                                );
                          updateField(withDefinitionDefault(field, nextValues));
                        }}
                        onSelect={(event) => event.preventDefault()}
                      >
                        {option.name}
                      </DropdownMenuCheckboxItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : layout === "form" && field.type === "boolean" ? (
              <label className="flex h-9 items-center gap-2 text-sm">
                <Checkbox
                  checked={field.defaultValue === true}
                  onCheckedChange={(checked) =>
                    updateField(withDefinitionDefault(field, checked === true))
                  }
                  disabled={rowDisabled}
                  aria-label="Default Value"
                />
                Enabled by default
              </label>
            ) : layout === "form" &&
              (field.type === "text" || field.type === "json") ? (
              <Textarea
                value={defaultValueToText(field.defaultValue)}
                onChange={(event) =>
                  updateField(
                    withDefinitionDefault(
                      field,
                      modalDefaultFromText(field.type, event.target.value),
                    ),
                  )
                }
                readOnly={rowDisabled}
                disabled={rowDisabled}
                placeholder={defaultPlaceholder(selectedVariable)}
                aria-label="Default Value"
                className={field.type === "json" ? "font-mono text-xs" : ""}
              />
            ) : (
              <Input
                type={
                  layout === "form"
                    ? modalDefaultInputType(field.type)
                    : undefined
                }
                step={
                  layout === "form" && field.type === "integer"
                    ? "1"
                    : undefined
                }
                value={
                  layout === "form" && field.type === "array"
                    ? jsonArrayToStringArray(field.defaultValue).join(", ")
                    : defaultValueToText(field.defaultValue)
                }
                onChange={(event) =>
                  updateField(
                    withDefinitionDefault(
                      field,
                      layout === "form"
                        ? modalDefaultFromText(field.type, event.target.value)
                        : event.target.value,
                    ),
                  )
                }
                readOnly={rowDisabled}
                disabled={rowDisabled}
                placeholder={defaultPlaceholder(selectedVariable)}
                aria-label="Default Value"
              />
            )}
          </div>
          {canResetDefault ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="shrink-0"
              aria-label="Reset to source default"
              onClick={() =>
                updateField(
                  withDefinitionDefault(
                    field,
                    selectedVariable.variable_default,
                  ),
                )
              }
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          ) : null}
        </div>,
      )}

      {presentField(
        "Required",
        <div
          className={cn(
            "flex min-w-0 items-center gap-2",
            layout === "row" && "justify-end",
          )}
        >
          <label className="flex h-9 items-center justify-center">
            <span className="sr-only">Required field</span>
            <Checkbox
              checked={field.required === true}
              onCheckedChange={(checked) =>
                updateField({ ...field, required: checked === true })
              }
              disabled={rowDisabled}
              aria-label="Required field"
            />
          </label>
          {onRemove ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={rowDisabled}
              onClick={() => onRemove(index)}
              aria-label="Remove input field"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          ) : null}
        </div>,
      )}
    </div>
  );
}
